import { gql, useLazyQuery, useApolloClient } from '@apollo/client'
import { SearchGraphql, PageInfo, ResultsList, Metadata } from '../../../../core/models'
import { User } from '../models'
import { useEffect, useState, useCallback, useMemo } from 'react'
import { Enterprise } from 'src/admin/scenes/entities/enterprises/models'
import { getAppBarState } from 'src/store'
import { fetchAllPages } from '../services/userPagination'

export const GET_USERS = gql`
  query GetUsers(
    $enterpriseId: String!
    $skip: Int
    $take: Int
    $where: UserWithClaimsFilterInput
    $order: [UserWithClaimsSortInput!]
  ) {
    enterpriseUsers(enterpriseId: $enterpriseId, where: $where, order: $order, take: $take, skip: $skip) {
      items {
        id
        userName
        email
        givenName
        familyName
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
      }
      totalCount
    }
  }
`

interface UsersData {
  enterpriseUsers: {
    totalCount: number
    items: User[]
    pageInfo: PageInfo
  }
}

export interface UseUsersOptions {
  fetchAll?: boolean
}

export const useUsers = (options?: UseUsersOptions) => {
  const fetchAll = options?.fetchAll
  const client = useApolloClient()
  const [isFetchingAll, setIsFetchingAll] = useState(false)
  const [fetchAllError, setFetchAllError] = useState<any>(null)

  const [getResults, { loading: lazyLoading, error: lazyError, data }] = useLazyQuery<UsersData>(GET_USERS, {
    fetchPolicy: 'network-only',
  })

  const [results, setResults] = useState<ResultsList<User, Metadata>>({
    results: [],
    metadata: {},
  })
  const { userEnterprise } = getAppBarState().appState

  useEffect(() => {
    if (!isFetchingAll && data?.enterpriseUsers) {
      setResults({
        results: data?.enterpriseUsers.items || [],
        metadata: {
          Count: data?.enterpriseUsers.totalCount,
        },
      })
    }
  }, [data, isFetchingAll])

  const loading = lazyLoading || isFetchingAll
  const error = lazyError || fetchAllError

  const getUsers = useCallback(
    async (search?: any, order?: any) => {
      const { skip, take, where } = search || {}
      const variables = {
        where: { ...where },
        enterpriseId: userEnterprise?.id,
        skip: skip ? skip : 0,
        take: take ? take : 100,
        order: order ? order : { userName: 'DESC' },
      }

      if (fetchAll) {
        setIsFetchingAll(true)
        setFetchAllError(null)
        try {
          const allItems = await fetchAllPages<User, any>(
            client,
            GET_USERS,
            variables,
            (data: UsersData) => ({
              items: data.enterpriseUsers.items,
              pageInfo: data.enterpriseUsers.pageInfo,
            })
          )
          setResults({
            results: allItems,
            metadata: {
              Count: allItems.length,
            },
          })
        } catch (err) {
          setFetchAllError(err)
        } finally {
          setIsFetchingAll(false)
        }
      } else {
        getResults({ variables })
      }
    },
    [getResults, userEnterprise?.id, fetchAll, client]
  )

  const users = useMemo(() => {
    return results.results.map((user) => {
      const clonedUser = { ...user }
      clonedUser.fullName = `${clonedUser.familyName} ${clonedUser.givenName}`
      return clonedUser
    })
  }, [results.results])
  return { loading, error, users, results, getUsers, isFetchingAll }
}
